/**
 * Project-local replacement for v3 `Data.tagged`, which was removed in v4.
 *
 * **Why a helper:** v4 dropped the single-tag `Data.tagged`. What it ships
 * instead fits a different shape: `Data.taggedEnum` needs the whole closed
 * union declared up front, and `Data.TaggedClass` is instantiated with `new`.
 * Each key here is one tag living alone in its own module, so a one-member
 * enum would be ceremony and a class would mean rewriting every call site
 * from `Key({ ... })` to `new Key({ ... })`. This wrapper keeps the v3 call
 * shape instead.
 *
 * Use `Data.taggedEnum` for a real closed union — see `shippingSelection.ts`
 * and the carrier `Frame` types. This helper is for cache keys only.
 *
 * v4 plain objects already have structural `Equal` semantics ("plain objects
 * compare enumerable keys recursively", `effect/dist/Equal.d.ts`), so the
 * wrapper carries no behavioral cost — the returned object is `Equal` to any
 * other object with the same `_tag` and properties.
 */
export const tagged =
  <T extends { readonly _tag: string }>(tag: T['_tag']) =>
  (args: Omit<T, '_tag'>): T =>
    ({ _tag: tag, ...args }) as T;
